<template>
  <div class="component">
    <div class="lead stk-p-8">
      <Stack>
        <header>
          <Title size="7">
            Hello {{ advisorStore.advisor?.name }}, what would you like to accomplish?
          </Title>
        </header>

        <Stack
          v-for="feature in features"
          :key="feature.title"
          class="feature"
        >
          <Stack class="feature-body" gap="4">
            <Badge
              v-if="feature.isEnabled"
              class="feature-badge"
              status="success"
            >
              Feature Enabled
            </Badge>

            <Badge
              v-else
              class="feature-badge"
            >
              Feature Not Enabled
            </Badge>

            <Stack gap="1">
              <Title>{{ feature.title }}</Title>
              <Subtitle size="5">
                {{ feature.subtitle }}
              </Subtitle>
            </Stack>

            <Buttons v-if="feature.isEnabled">
              <HomeButton
                v-for="button in feature.featureButtons"
                :key="button.id"
                :button="button"
              />
            </Buttons>

            <Buttons v-else>
              <HomeButton
                v-for="button in feature.infoLinks"
                :key="button.id"
                :button="button"
              />
            </Buttons>
          </Stack>
        </Stack>

        <template v-if="hasMercerSimilaritySearch">
          <Title>
            Tools
          </Title>

          <div>
            <HomeButton
              :button="{
                label: 'Go to Similarity Search',
                to: { name: 'mercer-similar-ticker-search' },
                variant: 'primary',
                iconBefore: 'fas fa-search',
              }"
            />
          </div>
        </template>
      </Stack>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import { useAdvisorStore } from "@frontend/ui/stores/advisorStore";
import HomeButton from '@frontend/ui/components/global/HomeButton.vue';
import { Badge, Button, Buttons, Stack, Subtitle, Title } from '@stakeui';

const advisorStore = useAdvisorStore();

const hasMeetings = computed(() => advisorStore.hasFeature('Meetings'));
const hasPortfolioCheckup = computed(() => advisorStore.hasFeature('Portfolio Checkup'));
const hasMercerSimilaritySearch = computed(() => advisorStore.hasFeature('Mercer Similarity Search'));

const features = computed(() => {
  const meetingsEnabled = hasMeetings.value
  const portfolioEnabled = hasPortfolioCheckup.value
  return [
    {
      title: 'Meeting Assistant',
      subtitle: 'Record, transcribe, and get AI-powered summaries + follow up actions for your meetings',
      isEnabled: meetingsEnabled,
      featureButtons: [
        {
          id: 'meetings',
          to: { name: 'meetings' },
          iconBefore: 'fas fa-microphone',
          label: 'Record a Meeting',
          variant: 'primary',
        },
        {
          id: 'meetings-tutorial',
          href: 'https://help.yourstake.org/knowledge/getting-started-with-the-ai-note-taker',
          iconBefore: 'fas fa-book',
          iconAfter: 'fas fa-external-link-alt',
          label: 'View Tutorial',
          target: '_blank',
        },
      ],
      // Show the info links if the feature is disabled
      infoLinks: [
        {
          id: 'meetings-tour',
          href: 'https://yourstake.org/product-tours/ai-note-taker',
          iconBefore: 'fas fa-info-circle',
          label: 'Learn about AI Meeting Notes',
          target: '_blank',
        },
      ],
    },
    {
      title: 'Portfolio Intelligence',
      subtitle: 'Upload client portfolios or prospect statements, analyze portfolios and generate proposals',
      isEnabled: portfolioEnabled,
      featureButtons: [
        {
          id: 'auto-portfolio',
          to: { name: 'auto-portfolio' },
          iconBefore: 'fas fa-rocket',
          label: 'Generate Proposal',
          variant: 'primary',
        },
        {
          id: 'past-advisor-core-analyses',
          to: { name: 'past-advisor-core-analyses' },
          iconBefore: 'fas fa-chart-bar',
          label: 'Past Analyses',
        },
        {
          id: 'auto-portfolio-tutorial',
          href: 'https://help.yourstake.org/knowledge/getting-started-with-advisor-core',
          iconBefore: 'fas fa-book',
          iconAfter: 'fas fa-external-link-alt',
          label: 'View Tutorial',
          target: '_blank',
        },
      ],
      infoLinks: [
        {
          id: 'auto-portfolio-tutorial',
          href: 'https://yourstake.org/advisor-core/portfolio-analysis-and-proposal-generation',
          iconBefore: 'fas fa-info-circle',
          iconAfter: 'fas fa-external-link-alt',
          label: 'Learn about Portfolio Analysis',
          target: '_blank',
        },
      ],
    },
    {
      title: 'ValuesCore',
      subtitle: "Navigate conversations to align clients' values with portfolios, and demonstrate impact over time",
      isEnabled: true,
      featureButtons: [
        {
          id: 'setup',
          to: { name: 'setup' },
          iconBefore: 'fas fa-hand-holding-heart',
          label: 'Gather Client Values',
          variant: 'primary',
        },
        {
          id: 'generate-report',
          href: '/ui/advisor/reports',
          iconBefore: 'fas fa-chart-bar',
          label: 'Generate Report',
        },
        {
          id: 'fund-screener-v2',
          href: '/advisor-dashboard/fund-screener-v2/',
          iconBefore: 'fas fa-search',
          label: 'Screen Funds',
        },
      ],
    },
  ];
});

const settingLinks = computed(() => {
  return [
    {
      href: "/accounts/password_reset/",
      icon: "fas fa-lock",
      label: "Change Password",
    },
    {
      href: "/two-factor-auth/",
      icon: "fas fa-shield-alt",
      label: "Two Factor Auth",
    },
    {
      href: "https://help.yourstake.org/knowledge/kb-tickets/new",
      icon: "fas fa-envelope",
      label: "Contact Us",
    },
    {
      to: { name: 'oauth-integration-dashboard' },
      icon: "fas fa-plug",
      label: "Manage Integrations",
    },
  ];
});
</script>

<style scoped>
.feature {
  border-color: var(--stk-control-border-color);
  border-style: solid;
  border-width: var(--stk-control-border-width);
  box-shadow: var(--stk-control-shadow);
  padding: 1.75em 2em;
  border-radius: 1em;
}

.feature-body {
  position: relative;
}

.feature-badge {
  position: absolute;
  right: 0;
  top: 0;
}

.side {
  background-color: var(--stk-background);
  border-left: 1px solid var(--stk-border);
  padding: 1.5em;
}
</style>
